import React from 'react';
import { Loader2, RotateCcw, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Switch } from '../../ui/switch';
import type { PerformanceScheduleDetail } from '../../../types/performance.types';
import { performanceApi } from '../../../utils/performance-api';
import { performancePillClass, reviewStatusTone } from '../../../utils/performance';
import { InitialsAvatar, PerformancePill } from '../PerformancePrimitives';

interface ParticipantsTabProps {
  detail: PerformanceScheduleDetail;
  departments: { id: string | number; departmentName: string; departmentCode?: string }[];
  onChanged: () => Promise<void> | void;
}

export const ParticipantsTab: React.FC<ParticipantsTabProps> = ({
  detail,
  departments,
  onChanged,
}) => {
  const [departmentId, setDepartmentId] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [savingId, setSavingId] = React.useState<number | null>(null);
  const [reopenTarget, setReopenTarget] = React.useState<{ id: number; name: string } | null>(null);
  const [reopenReason, setReopenReason] = React.useState('');
  const [reopening, setReopening] = React.useState(false);
  const readOnly = detail.schedule.status === 'Closed';

  const participants = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return detail.participants;
    return detail.participants.filter((participant) => (
      [
        participant.user.name,
        participant.user.jobRole,
        participant.user.department,
        participant.user.managerName,
        participant.exclusionReason,
      ].some((value) => String(value || '').toLowerCase().includes(needle))
    ));
  }, [detail.participants, query]);

  const update = async (
    userId: number,
    patch: { included?: boolean; exclusionReason?: string }
  ) => {
    setSavingId(userId);
    try {
      await performanceApi.settings.updateParticipant(
        detail.schedule.id,
        userId,
        patch
      );
      await onChanged();
    } finally {
      setSavingId(null);
    }
  };

  const openReopen = (userId: number, name: string) => {
    setReopenTarget({ id: userId, name });
    setReopenReason('');
  };

  const reopenPlan = async () => {
    if (!reopenTarget || !reopenReason.trim()) return;
    setReopening(true);
    try {
      await performanceApi.plan.reopen(
        detail.schedule.id,
        reopenTarget.id,
        reopenReason.trim()
      );
      toast.success(`${reopenTarget.name}'s goal plan unlocked`);
      setReopenTarget(null);
      setReopenReason('');
      await onChanged();
    } finally {
      setReopening(false);
    }
  };

  const bulkInclude = async () => {
    if (!departmentId) return;
    await performanceApi.settings.bulkInclude(detail.schedule.id, departmentId);
    toast.success('Everyone in the department included');
    await onChanged();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Participants</h3>
          <p className="text-sm text-muted-foreground">
            {detail.metrics.participants} included · {detail.metrics.excluded} excluded. Excluded
            people see the “not included” state and cannot set goals or review.
          </p>
        </div>
        {!readOnly ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Include a whole department</span>
              <Select value={departmentId} onValueChange={setDepartmentId}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Choose department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((department) => (
                    <SelectItem key={department.id} value={String(department.id)}>
                      {department.departmentName}
                      {department.departmentCode ? ` (${department.departmentCode})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={bulkInclude} disabled={!departmentId}>
              <Users className="mr-2 h-4 w-4" /> Include all
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search participants by name, role or department"
            className="pl-8"
          />
        </div>
        <span className="text-xs tabular-nums text-muted-foreground">
          {participants.length} of {detail.participants.length} participants
        </span>
      </div>

      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Person</th>
              <th className="px-3 py-2.5 text-left font-medium">Department</th>
              <th className="px-3 py-2.5 text-left font-medium">Goals</th>
              <th className="px-3 py-2.5 text-left font-medium">Plan</th>
              <th className="px-3 py-2.5 text-left font-medium">Mid-year</th>
              <th className="px-3 py-2.5 text-left font-medium">End-of-year</th>
              <th className="px-3 py-2.5 text-left font-medium">Included</th>
              <th className="px-3 py-2.5 text-left font-medium">Reason</th>
            </tr>
          </thead>
          <tbody>
            {participants.map((participant) => (
              <tr key={participant.user.id} className="border-t border-border">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <InitialsAvatar name={participant.user.name} />
                    <div>
                      <div className="font-medium">{participant.user.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {participant.user.jobRole}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  {participant.user.department || '–'}
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  {participant.included ? participant.goalsCount : '–'}
                </td>
                <td className="px-3 py-2.5">
                  {participant.included ? (
                    <div className="flex flex-col items-start gap-1">
                      <PerformancePill
                        tone={participant.planStatus === 'Submitted' ? 'success' : 'warning'}
                      >
                        {participant.planStatus}
                      </PerformancePill>
                      {participant.planStatus === 'Submitted' &&
                      detail.schedule.status === 'Active' ? (
                        <button
                          type="button"
                          onClick={() => openReopen(participant.user.id, participant.user.name)}
                          className="text-xs text-link hover:underline"
                        >
                          Unlock plan
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    '–'
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {participant.included ? (
                    <PerformancePill tone={reviewStatusTone(participant.reviews.mid)}>
                      {participant.reviews.mid}
                    </PerformancePill>
                  ) : (
                    <PerformancePill tone="muted">Excluded</PerformancePill>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {participant.included ? (
                    <PerformancePill tone={reviewStatusTone(participant.reviews.eoy)}>
                      {participant.reviews.eoy}
                    </PerformancePill>
                  ) : (
                    <PerformancePill tone="muted">Excluded</PerformancePill>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <Switch
                    checked={participant.included}
                    disabled={readOnly || savingId === participant.user.id}
                    onCheckedChange={(checked) =>
                      update(participant.user.id, {
                        included: checked,
                        exclusionReason: checked ? '' : 'Not included',
                      })
                    }
                  />
                </td>
                <td className="px-3 py-2.5">
                  {!participant.included ? (
                    <Input
                      defaultValue={participant.exclusionReason}
                      disabled={readOnly}
                      placeholder="Reason"
                      className="h-7 w-40 text-xs"
                      onBlur={(event) =>
                        update(participant.user.id, {
                          exclusionReason: event.target.value,
                        })
                      }
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!participants.length ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            No participants match “{query}”.
          </p>
        ) : null}
      </div>

      <div className="space-y-2 md:hidden">
        {!participants.length ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No participants match “{query}”.
          </p>
        ) : null}
        {participants.map((participant) => (
          <div key={participant.user.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <InitialsAvatar name={participant.user.name} />
                <div>
                  <div className="text-sm font-semibold">{participant.user.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {participant.user.jobRole}
                    {participant.user.department ? ` · ${participant.user.department}` : ''}
                  </div>
                </div>
              </div>
              <Switch
                checked={participant.included}
                disabled={readOnly || savingId === participant.user.id}
                onCheckedChange={(checked) =>
                  update(participant.user.id, {
                    included: checked,
                    exclusionReason: checked ? '' : 'Not included',
                  })
                }
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <PerformancePill tone={participant.planStatus === 'Submitted' ? 'success' : 'warning'}>
                Plan {participant.planStatus}
              </PerformancePill>
              {participant.planStatus === 'Submitted' &&
              detail.schedule.status === 'Active' ? (
                <button
                  type="button"
                  onClick={() => openReopen(participant.user.id, participant.user.name)}
                  className="text-xs text-link hover:underline"
                >
                  Unlock
                </button>
              ) : null}
              <PerformancePill tone={reviewStatusTone(participant.reviews.mid)}>
                Mid: {participant.reviews.mid}
              </PerformancePill>
              <PerformancePill tone={reviewStatusTone(participant.reviews.eoy)}>
                EOY: {participant.reviews.eoy}
              </PerformancePill>
            </div>
            {!participant.included ? (
              <Input
                defaultValue={participant.exclusionReason}
                disabled={readOnly}
                placeholder="Reason"
                className="mt-2 h-8 text-xs"
                onBlur={(event) =>
                  update(participant.user.id, { exclusionReason: event.target.value })
                }
              />
            ) : null}
          </div>
        ))}
      </div>

      <Dialog
        open={Boolean(reopenTarget)}
        onOpenChange={(open) => {
          if (!open) setReopenTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unlock goal plan</DialogTitle>
            <DialogDescription>
              {reopenTarget?.name} can edit goals, targets and weights and must submit again.
              Progress and notes are unaffected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reopen-reason">Reason</Label>
            <Textarea
              id="reopen-reason"
              value={reopenReason}
              onChange={(event) => setReopenReason(event.target.value)}
              rows={3}
              placeholder="Why does the plan need to change?"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenTarget(null)}>
              Cancel
            </Button>
            <Button onClick={reopenPlan} disabled={reopening || !reopenReason.trim()}>
              {reopening ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Unlock plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
